import React, { useState, useEffect, useRef } from "react";
import { portfolio } from "@/data/portfolio";
import { ArrowUpRight } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "@/context/ThemeContext";

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const { theme } = useTheme();
  const { meta } = portfolio;

  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const identityRef = useRef(null);
  const proofRef = useRef(null);
  const progressRef = useRef(null);

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [canvasReady, setCanvasReady] = useState(false);

  const dayFrameCount = 73;
  const nightFrameCount = 75;
  const fallbackFrame = theme === "day" ? "/video_frames/frame_001.webp" : "/video_night_frames/frame_001.webp";

  const dayImagesRef = useRef([]);
  const nightImagesRef = useRef([]);
  const scrollProgressRef = useRef(0);
  const canvasReadyRef = useRef(false);

  /* Preload frames */
  useEffect(() => {
    let loadedCount = 0;
    const totalFrames = dayFrameCount + nightFrameCount;

    const dayImagesArray = [];
    const nightImagesArray = [];

    const getDayFrameUrl = (index) => {
      const paddedNum = String(index).padStart(3, "0");
      return `/video_frames/frame_${paddedNum}.webp`;
    };

    const getNightFrameUrl = (index) => {
      const paddedNum = String(index).padStart(3, "0");
      return `/video_night_frames/frame_${paddedNum}.webp`;
    };

    const checkComplete = () => {
      loadedCount++;
      setLoadProgress(Math.round((loadedCount / totalFrames) * 100));
      if (loadedCount === totalFrames) {
        setImagesLoaded(true);
      }
    };

    const drawInitialFrame = (loadedTheme) => {
      if (scrollProgressRef.current !== 0) return;
      requestAnimationFrame(() => drawFrame(0, loadedTheme));
    };

    // Preload Day
    for (let i = 1; i <= dayFrameCount; i++) {
      const img = new Image();
      img.src = getDayFrameUrl(i);
      img.onload = () => {
        if (i === 1) drawInitialFrame("day");
        checkComplete();
      };
      img.onerror = checkComplete;
      dayImagesArray.push(img);
    }

    // Preload Night
    for (let i = 1; i <= nightFrameCount; i++) {
      const img = new Image();
      img.src = getNightFrameUrl(i);
      img.onload = () => {
        if (i === 1 && theme === "night") drawInitialFrame("night");
        checkComplete();
      };
      img.onerror = checkComplete;
      nightImagesArray.push(img);
    }

    dayImagesRef.current = dayImagesArray;
    nightImagesRef.current = nightImagesArray;
  }, []);

  /* Draw current frame helper */
  const drawFrame = (index, currentTheme) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const targetWidth = canvas.clientWidth || window.innerWidth;
    const targetHeight = canvas.clientHeight || window.innerHeight;
    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    const ctx = canvas.getContext("2d");
    
    const activeTheme = currentTheme || theme;
    const isDay = activeTheme === "day";
    const images = isDay ? dayImagesRef.current : nightImagesRef.current;

    const img = images[index];

    if (img && ctx) {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Implement cover layout behavior on canvas
      const imgRatio = img.width / img.height;
      const canvasRatio = canvas.width / canvas.height;

      let drawWidth, drawHeight, offsetX, offsetY;

      if (imgRatio > canvasRatio) {
        // Image is wider than canvas
        drawHeight = canvas.height;
        drawWidth = canvas.height * imgRatio;
        const isMobile = window.innerWidth < 768;
        const alignRatio = isMobile ? 0.28 : 0.8;
        offsetX = (canvas.width - drawWidth) * alignRatio;
        offsetY = 0;
      } else {
        // Image is taller than canvas
        drawWidth = canvas.width;
        drawHeight = canvas.width / imgRatio;
        offsetX = 0;
        offsetY = (canvas.height - drawHeight) * 0.5; // Center vertically
      }

      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      if (!canvasReadyRef.current) {
        canvasReadyRef.current = true;
        setCanvasReady(true);
      }
    }
  };

  /* Setup Canvas sizing */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      if (imagesLoaded) {
        const isDay = theme === "day";
        const count = isDay ? dayFrameCount : nightFrameCount;
        const currentFrame = Math.round(scrollProgressRef.current * (count - 1));
        drawFrame(currentFrame, theme);
      }
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();

    return () => window.removeEventListener("resize", resizeCanvas);
  }, [imagesLoaded, theme]);

  /* Redraw when theme changes */
  useEffect(() => {
    if (!imagesLoaded) return;
    const isDay = theme === "day";
    const count = isDay ? dayFrameCount : nightFrameCount;
    const currentFrame = Math.round(scrollProgressRef.current * (count - 1));
    drawFrame(currentFrame, theme);
  }, [theme, imagesLoaded]);

  /* GSAP scroll trigger for scrubbing frames */
  useEffect(() => {
    if (!imagesLoaded) return;

    const section = sectionRef.current;
    const identity = identityRef.current;
    const proof = proofRef.current;
    const bar = progressRef.current;
    if (!section || !identity || !proof) return;

    const ctx = gsap.context(() => {
      // Create a dummy object to track normalized progress (0 to 1)
      const stateObj = { progress: 0 };

      gsap.to(stateObj, {
        progress: 1,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "+=200%",
          scrub: true,
          pin: true,
          pinSpacing: false,
          onUpdate: (self) => {
            const p = Math.min(Math.max(self.progress, 0), 1);
            scrollProgressRef.current = p;

            // Draw correct frame at the current scrub progress percentage
            const isDay = theme === "day";
            const count = isDay ? dayFrameCount : nightFrameCount;
            const currentFrame = Math.round(p * (count - 1));
            drawFrame(currentFrame, theme);

            // Progress bar update
            if (bar) bar.style.transform = `scaleX(${p})`;

            // Panel transitions mapped to primary hero section scroll progress
            const tStart = 0.45;
            const tEnd = 0.70;
            let t = 0;
            if (p > tStart) {
              t = Math.min((p - tStart) / (tEnd - tStart), 1);
            }

            const easeT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            if (identity) {
              identity.style.opacity = 1 - easeT;
              identity.style.transform = `translateY(${-easeT * 32}px)`;
            }

            if (proof) {
              proof.style.opacity = easeT;
              proof.style.transform = `translateY(${(1 - easeT) * 42}px)`;
              if (easeT > 0.05) {
                proof.style.pointerEvents = "auto";
              } else {
                proof.style.pointerEvents = "none";
              }
            }
          },
        },
      });
    }, section);

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [imagesLoaded, theme]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={sectionRef}
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* ─── LAYER 1: Sticky Canvas for Video Frames ─── */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100vh",
          overflow: "hidden",
          background: "#000",
        }}
      >

        <img
          src={fallbackFrame}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "80% center",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        <canvas
          ref={canvasRef}
          style={{
            display: "block",
            position: "relative",
            width: "100%",
            height: "100%",
            zIndex: 1,
            opacity: canvasReady ? 1 : 0,
            transition: "opacity 180ms ease",
          }}
        />

        {/* Day/Night responsive tint overlays for legibility */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: theme === "day" ? "rgba(255,255,255,0.15)" : "rgba(10,10,10,0.40)",
            transition: "background-color 600ms ease",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />



        {/* Bottom fade into next section */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "35%",
            background: theme === "day"
              ? "linear-gradient(to top, rgba(243,237,225,0.9) 0%, transparent 100%)"
              : "linear-gradient(to top, rgba(13,13,13,0.95) 0%, transparent 100%)",
            transition: "background 600ms ease",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        {/* Progress bar (hidden) */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background: "rgba(255,255,255,0.08)",
            zIndex: 30,
            display: "none",
          }}
        >
          <div
            ref={progressRef}
            style={{
              height: "100%",
              transformOrigin: "left",
              transform: "scaleX(0)",
              background: "var(--accent)",
            }}
          />
        </div>
      </div>

      {/* ─── LAYER 2: Overlay scroll content (sits on top of sticky backdrop) ─── */}
      <div
        className="hero-content"
        style={{
          position: "relative",
          zIndex: 10,
          marginTop: "-100vh",
          minHeight: "300vh",
          pointerEvents: "none",
        }}
      >
        <div
          className="hero-inner"
          style={{
            position: "sticky",
            top: 0,
            margin: "0 auto",
            maxWidth: "1340px",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "1.25rem",
            padding: "8rem 3rem 3rem",
          }}
        >
          {/* Panel 1: Identity */}
          <div
            ref={identityRef}
            id="panel-identity"
            data-hero-panel="identity"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2.5rem",
              minHeight: "calc(100vh - 11rem)",
              justifyContent: "center",
              willChange: "opacity, transform",
              marginLeft: "auto",
              maxWidth: "36rem",
              width: "100%",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
                {/* Meta bar */}
                <div className="flex items-center gap-3 font-mono text-[0.65rem] sm:text-[0.7rem] uppercase tracking-microcap text-white/55">
                  <span className="text-white/85">{meta.volume}</span>
                  <span className="slash" />
                  <span>{meta.location}</span>
                </div>

                <h1 aria-label={meta.name} className="hero-name">
                  <span className="word">
                    <span>{meta.nameSplit.first}</span>
                  </span>
                  <span className="word">
                    <span className="display-italic" style={{ color: "var(--accent)" }}>
                      {meta.nameSplit.last}
                    </span>
                  </span>
                </h1>
                {/* Mobile-only role line */}
                <p className="hero-role hero-role-mobile" aria-hidden="true">
                  {meta.status}
                </p>
              </div>

              {/* Desktop tagline + role */}
              <div className="hero-tagline-group" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <p className="hero-role" aria-hidden="true">
                  {meta.status}
                </p>
                <p className="hero-tagline">
                  {meta.tagline}
                </p>
              </div>

              <a href="#signal" className="hero-cta pointer-events-auto">
                <span>Get in touch</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10m0 0L8 3m5 5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>

          </div>

          {/* Panel 2: Proof (desktop only, scroll-revealed) */}
          <div
            ref={proofRef}
            id="panel-proof"
            data-hero-panel="proof"
            aria-hidden="true"
            style={{
              pointerEvents: "none",
              position: "absolute",
              right: "var(--hero-panel-edge, 3rem)",
              bottom: "var(--hero-panel-edge, 3rem)",
              left: "var(--hero-panel-edge, 3rem)",
              opacity: 0,
              transform: "translateY(42px)",
              willChange: "opacity, transform",
            }}
          >
            <div className="proof-inner" style={{ marginLeft: "auto" }}>
              <div className="proof-eyebrow">
                <span>Selected proof</span>
                <span className="proof-rule" aria-hidden="true" />
                <span>Production scale</span>
              </div>

              <div className="proof-stats">
                {portfolio.selectedProof.map((item, i) => (
                  <div key={i} className="stat-item">
                    <span className="stat-num">
                      {item.value}
                      {item.suffix && <span style={{ fontSize: "0.45em", marginLeft: "2px" }}>{item.suffix}</span>}
                    </span>
                    <span className="stat-label">{item.label}</span>
                  </div>
                ))}
              </div>

              <p className="proof-body">
                {portfolio.proofLine}
              </p>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
                <a href="#signal" className="hero-cta pointer-events-auto" style={{ animation: "none" }}>
                  <span>Get in touch</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8h10m0 0L8 3m5 5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
